/**
 * GaugeChart — Reliability gauge chart
 */

import { lazy, Suspense, useMemo } from "react";

const ReactECharts = lazy(() => import("echarts-for-react"));

interface GaugeChartProps {
  value: number;
  max?: number;
  label?: string;
  height?: number;
}

export function GaugeChart({
  value,
  max = 100,
  label = "",
  height = 200,
}: GaugeChartProps) {
  const option = useMemo(() => {
    return {
      backgroundColor: "transparent",
      series: [
        {
          type: "gauge" as const,
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max,
          progress: {
            show: true,
            width: 14,
            itemStyle: {
              color:
                value / max > 0.7
                  ? "#22c55e"
                  : value / max > 0.4
                    ? "#eab308"
                    : "#ef4444",
            },
          },
          pointer: { show: false },
          axisLine: {
            lineStyle: {
              width: 14,
              color: [[1, "rgba(255,255,255,0.08)"]],
            },
          },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          detail: {
            valueAnimation: true,
            fontSize: 20,
            fontWeight: "bold" as const,
            color: "inherit",
            offsetCenter: [0, "10%"],
            formatter: `{value}%`,
          },
          title: {
            show: !!label,
            offsetCenter: [0, "35%"],
            fontSize: 11,
            color: "rgba(255,255,255,0.5)",
          },
          data: [{ value: Math.round(value * 10) / 10, name: label }],
          animationDuration: 1000,
        },
      ],
    };
  }, [value, max, label, height]);

  return (
    <Suspense fallback={<div style={{ height: `${height}px` }} />}>
      <ReactECharts
        option={option}
        style={{ height: `${height}px`, width: "100%" }}
        opts={{ renderer: "canvas" }}
      />
    </Suspense>
  );
}
